import React from 'react'

import { Alert, Anchor, Loader, Menu, Stack, Text } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import type { RevisionsPluginState } from '#choo-app/lib/editor/plugins/revisions'
import { Toolbar } from '#components/Toolbar'
import { useMst } from '#hooks'
import { ButtonMode } from '#state/types'

import { NumberingItems } from './ExtrasMenu/NumberingItems'
import { PaidMenuItem } from './ExtrasMenu/PaidMenuItem'
import { PaidMenuNote } from './ExtrasMenu/PaidMenuNote'

interface ProductionMenuProps {
  mode?: ButtonMode
  openRevisionsModal(): void
}

function ProductionMenu(props: {
  mode?: ButtonMode
  canManageRevisions: boolean
  isCurrentOrgAdmin: boolean
  revisionsEnabled: boolean
  revisions: RevisionsPluginState
  focusEditor: () => void
  openRevisionsModal: () => void
}) {
  'use memo'

  const {
    mode = 'normal',
    canManageRevisions,
    isCurrentOrgAdmin,
    revisionsEnabled,
    revisions,
    focusEditor,
    openRevisionsModal,
  } = props

  return (
    <Menu
      position="bottom-end"
      width={canManageRevisions ? 250 : undefined}
      onClose={focusEditor}
    >
      <Menu.Target>
        <Toolbar.Button
          label="Production"
          icon="fa-clapperboard"
          dropdown
          mode={mode}
        />
      </Menu.Target>
      <Menu.Dropdown>
        <NumberingItems />
        <Menu.Divider />
        {revisionsEnabled ? (
          <Menu.Item
            disabled={!canManageRevisions || revisions.tag === 'Loading'}
            onClick={openRevisionsModal}
            rightSection={
              revisions.tag === 'Resolved' ? (
                <OnOffStatus enabled={revisions.revisionsState.enabled} />
              ) : revisions.tag === 'Inactive' ? (
                <OnOffStatus enabled={false} />
              ) : revisions.tag === 'Loading' ? (
                <Loader size="xs" />
              ) : undefined
            }
          >
            Revisions
          </Menu.Item>
        ) : (
          <PaidMenuItem title="Revisions" isUnpaidOrg />
        )}
        <PaidMenuNote
          features={[
            { name: 'scene numbering' },
            { name: 'page-locking' },
            {
              name: 'revisions',
              plural: true,
              available: revisionsEnabled,
              plans: ['Pro', 'Studio'],
            },
          ]}
        />
        {revisionsEnabled && !canManageRevisions && (
          <Alert color="gray" p={10}>
            <Stack gap="xs">
              <Text c="dimmed" fz="sm">
                You do not have the revisions permission enabled.
              </Text>
              {isCurrentOrgAdmin ? (
                <Text fz="sm">
                  <Anchor fz="sm" href="/settings/org/permissions">
                    Manage production permissions
                  </Anchor>
                </Text>
              ) : (
                <Text fz="sm" c="dimmed">
                  Contact your workspace admin to enable revisions.
                </Text>
              )}
            </Stack>
          </Alert>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}

function OnOffStatus({ enabled }: { enabled: boolean }) {
  'use memo'

  return (
    <Text c={enabled ? 'green.6' : 'dimmed'} size="xs" fw={700}>
      {enabled ? 'ON' : 'OFF'}
    </Text>
  )
}

// MST boilerplate

export { ObservedProductionMenu as ProductionMenu }

const ObservedProductionMenu = observer<ProductionMenuProps>(
  function ProductionMenu(props) {
    const { currentOrg, currentScript, user } = useMst()

    if (!currentScript || !currentOrg) return null

    return (
      <MemoizedProductionMenu
        mode={props.mode}
        canManageRevisions={user.canManageRevisions}
        isCurrentOrgAdmin={user.isCurrentOrgAdmin}
        revisionsEnabled={currentOrg.revisionsEnabled}
        revisions={currentScript.revisions}
        focusEditor={() => currentScript.observableEditor?.focusEditor()}
        openRevisionsModal={props.openRevisionsModal}
      />
    )
  },
)

const MemoizedProductionMenu = React.memo(ProductionMenu)
